import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  alpha,
  Alert
} from '@mui/material';
import {
  ArrowUpward as TopIcon,
  ArrowDownward as BottomIcon,
  Person as FullBodyIcon
} from '@mui/icons-material';
import { useDesigner } from '../../contexts/DesignerContext';

const BodyPartSelection: React.FC = () => {
  const { selections, setSelectedBodyPart, setGarment, setBodyPart } = useDesigner();

  const handleBodyPartChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newBodyPart = event.target.value as 'top' | 'bottom' | 'full';
    // prefer the canonical setter; keep alias for safety
    (setSelectedBodyPart || setBodyPart)(newBodyPart);
    
    // Clear incompatible garment selections when changing body parts
    if (selections.garmentType && selections.garment[selections.garmentType]) {
      // Reset garment selection if needed based on body part rules
      const garmentType = selections.garmentType;
      
      // Define garment categories by body part
      const topGarments = ['t-shirt', 'shirt', 'blouse', 'jacket', 'sweater', 'top'];
      const bottomGarments = ['pants', 'trousers', 'skirt', 'shorts', 'leggins', 'leggings'];
      const fullBodyGarments = ['dress', 'coat', 'dress-skirt'];
      
      // Check if current garment type is compatible with the new body part
      const isIncompatible = 
        (newBodyPart === 'top' && !topGarments.includes(garmentType)) ||
        (newBodyPart === 'bottom' && !bottomGarments.includes(garmentType)) ||
        (newBodyPart === 'full' && !fullBodyGarments.includes(garmentType));
      
      // If incompatible, clear the current garment selection
      if (isIncompatible) {
        console.log(`Clearing incompatible garment selection (${garmentType}) for body part ${newBodyPart}`);
        // We don't clear garmentType so user knows what was cleared
        // But we clear the actual garment image
        setGarment(garmentType, '');
      }
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 0, mb: 4, borderRadius: 3, overflow: 'hidden', boxShadow: '0 6px 18px rgba(0, 0, 0, 0.08)' }}>
      {/* Header Section */}
      <Box sx={{ 
        p: 3, 
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)', 
        backgroundColor: 'rgba(25, 118, 210, 0.03)', 
        display: 'flex', 
        alignItems: 'center' 
      }}>
        <FullBodyIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
        <Typography variant="h5" fontWeight={600} sx={{ color: 'primary.main', letterSpacing: '-0.01em' }}>
          Wybierz część ciała
        </Typography>
      </Box>
      
      <Box sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Na jaką część ciała ma zostać nałożona odzież:
        </Typography>
        
        {selections.garmentType && (() => {
          // Define garment categories by body part
          const topGarments = ['t-shirt', 'shirt', 'blouse', 'jacket', 'sweater', 'top'];
          const bottomGarments = ['pants', 'trousers', 'skirt', 'shorts', 'leggins', 'leggings'];
          const fullBodyGarments = ['dress', 'coat', 'dress-skirt'];
          
          const garmentType = selections.garmentType;
          const incompatibleMessages = {
            top: !topGarments.includes(garmentType) ? 
              `Wybrane ubranie typu "${garmentType}" nie jest kompatybilne z górną częścią ciała. Wybierz inny typ odzieży.` : null,
            bottom: !bottomGarments.includes(garmentType) ? 
              `Wybrane ubranie typu "${garmentType}" nie jest kompatybilne z dolną częścią ciała. Wybierz inny typ odzieży.` : null,
            full: !fullBodyGarments.includes(garmentType) ? 
              `Wybrane ubranie typu "${garmentType}" nie jest kompatybilne z całym ciałem. Wybierz inny typ odzieży.` : null
          };
          
          const message = incompatibleMessages[selections.bodyPart || 'full'];
          
          return message ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {message}
            </Alert>
          ) : null;
        })()}
        
        <RadioGroup
          value={selections.bodyPart || 'full'}
          onChange={handleBodyPartChange}
          sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', flexWrap: 'wrap' }}
        >
          <Tooltip title="Wybierz aby nałożyć odzież na górną część ciała (koszulka, bluza, itp.)">
            <FormControlLabel
              value="top"
              control={
                <Radio
                  sx={{
                    '&.Mui-checked': {
                      color: 'primary.main',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ 
                  textAlign: 'center',
                  p: 2,
                  borderRadius: 2,
                  border: selections.bodyPart === 'top' ? '2px solid' : '2px solid transparent',
                  borderColor: 'primary.main',
                  bgcolor: selections.bodyPart === 'top' ? alpha('#1976d2', 0.1) : 'transparent',
                  transition: 'all 0.2s',
                  width: 150
                }}>
                  <TopIcon sx={{ fontSize: 40, mb: 1, color: selections.bodyPart === 'top' ? 'primary.main' : 'text.secondary' }} />
                  <Typography variant="body1" fontWeight={selections.bodyPart === 'top' ? 600 : 400}>
                    Góra / Top
                  </Typography>
                </Box>
              }
              sx={{ mr: 2, alignItems: 'flex-start' }}
            />
          </Tooltip>
          
          <Tooltip title="Wybierz aby nałożyć odzież na dolną część ciała (spodnie, spódnica, itp.)">
            <FormControlLabel
              value="bottom"
              control={
                <Radio
                  sx={{
                    '&.Mui-checked': {
                      color: 'primary.main',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ 
                  textAlign: 'center',
                  p: 2,
                  borderRadius: 2,
                  border: selections.bodyPart === 'bottom' ? '2px solid' : '2px solid transparent',
                  borderColor: 'primary.main',
                  bgcolor: selections.bodyPart === 'bottom' ? alpha('#1976d2', 0.1) : 'transparent',
                  transition: 'all 0.2s',
                  width: 150
                }}>
                  <BottomIcon sx={{ fontSize: 40, mb: 1, color: selections.bodyPart === 'bottom' ? 'primary.main' : 'text.secondary' }} />
                  <Typography variant="body1" fontWeight={selections.bodyPart === 'bottom' ? 600 : 400}>
                    Dół / Bottom
                  </Typography>
                </Box>
              }
              sx={{ mr: 2, alignItems: 'flex-start' }}
            />
          </Tooltip>
          
          <Tooltip title="Wybierz aby nałożyć odzież na całe ciało (sukienka, kombinezon, itp.)">
            <FormControlLabel
              value="full"
              control={
                <Radio
                  sx={{
                    '&.Mui-checked': {
                      color: 'primary.main',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ 
                  textAlign: 'center',
                  p: 2,
                  borderRadius: 2,
                  border: selections.bodyPart === 'full' ? '2px solid' : '2px solid transparent',
                  borderColor: 'primary.main',
                  bgcolor: selections.bodyPart === 'full' ? alpha('#1976d2', 0.1) : 'transparent',
                  transition: 'all 0.2s',
                  width: 150
                }}>
                  <FullBodyIcon sx={{ fontSize: 40, mb: 1, color: selections.bodyPart === 'full' ? 'primary.main' : 'text.secondary' }} />
                  <Typography variant="body1" fontWeight={selections.bodyPart === 'full' ? 600 : 400}>
                    Całe Ciało / Full Body
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Tooltip>
        </RadioGroup>
      </Box>
    </Paper>
  );
};

export default BodyPartSelection;
